import { ContentCopy } from "@mui/icons-material";
import { Button, Card, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlDeliverableTableRow } from "~/__generated__";

import { ProjectDeliverablesScreen_TableFragment } from "../../fragments.generated";
import { useClipboardToTable } from "./useTableToClipboard";

type ProjectDeliverableTableProps = {
    table: ProjectDeliverablesScreen_TableFragment;
};

export function ProjectDeliverableTable({ table }: ProjectDeliverableTableProps) {
    const { t } = useTranslation("project");

    const { copied, handleCopy } = useClipboardToTable(table);

    const handleRowClick = (row: GqlDeliverableTableRow) => {
        if (row.link === undefined || row.link === null) return;
        window.open(row.link, "_blank");
    };

    if (table.rows.length === 0) {
        return <EmptyState title={table.title} />;
    }

    return (
        <Stack rowGap={1 / 2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1">{table.title}</Typography>
                <Button disabled={copied} startIcon={<ContentCopy />} onClick={handleCopy} size="small">
                    {t("deliverablesScreen.copyBtn")}
                </Button>
            </Stack>

            <TableContainer component={Card} variant="outlined">
                <Table
                    sx={{
                        // The last row's cell borders would stack on the Card's
                        // outlined border — remove them so the bottom shows a
                        // single clean border.
                        "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {table.columns.map(column => (
                                <TableCell key={column.key}>{column.title}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {table.rows.map(row => (
                            <TableRow
                                key={row.rowId}
                                onClick={() => handleRowClick(row)}
                                sx={{
                                    cursor: row.link ? "pointer" : undefined,
                                }}
                            >
                                {row.columns.map(cell => (
                                    <TableCell key={cell.key} colSpan={cell.colSpan ?? 1}>
                                        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: needed to have line breaks */}
                                        <div dangerouslySetInnerHTML={{ __html: newLineToLineBreak(cell.value) }} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
}

type EmptyStateProps = {
    title: string;
};

function EmptyState({ title }: EmptyStateProps) {
    const { t } = useTranslation("project");
    return (
        <Stack rowGap={1 / 2}>
            <Typography variant="subtitle1">{title}</Typography>
            <Card sx={{ p: 3 }} variant="outlined">
                <Typography variant="body2" color="text.secondary">
                    {t("deliverablesScreen.noData")}
                </Typography>
            </Card>
        </Stack>
    );
}

function newLineToLineBreak(input: string): string {
    return input.replace(/\n/g, "<br>");
}
